import type { NotificationDto } from '@fluenceur/types';
import {
  CollabType,
  MentionBoostDeliveryState,
  NotificationCategory,
  NotificationType,
} from '@fluenceur/types';
import {
  COLLAB_TRACKING_PATH,
  collabTrackingHref,
} from '@/pages/collabs/suivi/collab-tracking-href';

/** Fallback destination when the backend did not carry a per-event deep link. */

/**
 * The tracking list opened on one collab, which `useCollabDeepLink` turns into
 * the collab's own panel.
 *
 * Built here rather than served: the notification registry declares every
 * collab event with `defaultActionUrl: null` and the inbox stores exactly that,
 * so no collab notification ever arrives with a destination of its own. The
 * list alone was the honest fallback while nothing better existed, but it makes
 * the reader find the row again — and on a payment that just became possible,
 * that stands between the brand and the charge it was told it could make.
 */
const collabDeepLink = (collabId: string): string =>
  collabId ? collabTrackingHref(collabId) : COLLAB_TRACKING_PATH;

export function getNotificationHref(notification: NotificationDto): string | null {
  if (notification.type === NotificationType.BRAND_SPACE_APPLICATION_RECEIVED) {
    return '/applications?applicationStatus=pending';
  }

  if (notification.type === NotificationType.BRIEF_APPLICATION_RECEIVED) {
    return `/applications?c=${encodeURIComponent(notification.data.applicationId)}`;
  }

  if (notification.type === NotificationType.AGENT_RECOMMENDATIONS_DELIVERED) {
    return `/creators/discovery?tab=recommendations&batchId=${notification.data.batchId}`;
  }

  if (
    notification.type === NotificationType.AFFILIATE_JOINED_PROGRAM ||
    notification.type === NotificationType.GIFT_CLAIMED
  ) {
    return `/programs/${notification.data.programId}/affiliates`;
  }

  if (
    notification.type === NotificationType.BOOST_AUTHORIZATION_UPDATED ||
    notification.type === NotificationType.BOOST_DELIVERY_UPDATED ||
    notification.type === NotificationType.BOOST_DISPLAY_RIGHTS_UPDATED
  ) {
    return notification.data.mentionId
      ? `/mentions?previewMention=${notification.data.mentionId}`
      : null;
  }

  if (notification.type === NotificationType.CONVERSATION_CREATOR_MESSAGE_RECEIVED) {
    return `/messages/${encodeURIComponent(notification.data.conversationId)}`;
  }

  const actionUrl = notification.actionUrl;

  if (notification.category === NotificationCategory.COLLAB) {
    // A destination the server named still wins: the day the registry carries
    // one per event, it knows something this construction does not.
    return actionUrl ? normalizeActionUrl(actionUrl) : collabDeepLink(notification.data.collabId);
  }

  if (!actionUrl) return null;

  return normalizeActionUrl(actionUrl);
}

function normalizeActionUrl(actionUrl: string): string {
  return actionUrl.replace(/^\/dashboard/, '') || '/dashboard';
}

/**
 * The one attention state that carries a machine reason code to interpolate
 * into its body copy. Shared so the tile and the copy cannot drift apart.
 */
export function isBoostNeedsAttention(
  notification: NotificationDto
): notification is Extract<
  NotificationDto,
  { type: typeof NotificationType.BOOST_DELIVERY_UPDATED }
> {
  return (
    notification.type === NotificationType.BOOST_DELIVERY_UPDATED &&
    notification.data.deliveryState === MentionBoostDeliveryState.NEEDS_ATTENTION
  );
}

/**
 * True for the states that need the operator to fix something. These render the
 * amber warning tile instead of the neutral agent/actor avatar.
 */
export function isAttentionNotification(notification: NotificationDto): boolean {
  if (
    notification.type === NotificationType.COLLAB_DELIVERY_FAILED ||
    notification.type === NotificationType.COLLAB_PAYMENT_FAILED
  )
    return true;

  return isBoostNeedsAttention(notification);
}

/**
 * Inline call-to-action label key, or null when the row's own click target is
 * enough. Kept deliberately sparse: a button on every row reads as noise.
 */
export function getNotificationActionKey(notification: NotificationDto): string | null {
  if (notification.type === NotificationType.BRAND_SPACE_APPLICATION_RECEIVED) {
    return 'notifications.actions.viewApplications';
  }

  if (notification.type === NotificationType.BRIEF_APPLICATION_RECEIVED) {
    return 'notifications.actions.reviewApplication';
  }

  if (notification.type === NotificationType.AGENT_RECOMMENDATIONS_DELIVERED) {
    return 'notifications.actions.viewRecommendations';
  }

  if (notification.type === NotificationType.COLLAB_ACCEPTED) {
    return 'notifications.actions.openCollab';
  }

  if (isAttentionNotification(notification)) {
    return 'notifications.actions.viewDetails';
  }

  return null;
}

/** Name backing the avatar fallback initials. Empty string when unknown. */
export function getNotificationActorName(notification: NotificationDto): string {
  if (notification.actor?.name) return notification.actor.name;

  if (
    notification.type === NotificationType.AFFILIATE_JOINED_PROGRAM ||
    notification.type === NotificationType.GIFT_CLAIMED
  ) {
    return notification.data.affiliateName;
  }

  if (
    notification.type === NotificationType.COLLAB_VIEWED ||
    notification.type === NotificationType.COLLAB_ACCEPTED ||
    notification.type === NotificationType.COLLAB_DECLINED ||
    notification.type === NotificationType.COLLAB_DELIVERY_FAILED ||
    notification.type === NotificationType.BRIEF_APPLICATION_RECEIVED
  ) {
    return notification.data.creatorDisplayName;
  }

  if (
    notification.type === NotificationType.BOOST_AUTHORIZATION_UPDATED ||
    notification.type === NotificationType.BOOST_DELIVERY_UPDATED
  ) {
    return notification.data.creatorName;
  }

  return '';
}

/**
 * The parameters a notification's title and body are written with. The
 * backend sends facts, not words: a country travels as its ISO code and is
 * named here in the reader's language ("Pologne", "Poland"), or kept as the
 * code when no name exists for it; the first end of UGC rights travels as an
 * instant and is written as its UTC day; the instant a UGC content is validated
 * by silence is written with its time in the brand's zone, the zone named.
 */
export function getNotificationI18nParams(
  notification: NotificationDto,
  language: string
): NotificationDto['i18nParams'] {
  if (isUgcContentToReview(notification)) {
    const at = notification.i18nParams.tacitValidationDueAt;
    if (typeof at !== 'string') return notification.i18nParams;
    // The brand's zone, named, as the email writes it: never a time read in another zone.
    const zone = notification.i18nParams.tacitValidationTimeZone;
    const when = new Date(at).toLocaleString(language, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: typeof zone === 'string' ? zone : 'Europe/Paris',
      timeZoneName: 'short',
    });
    return { ...notification.i18nParams, tacitValidationDueAt: when };
  }
  if (notification.type === NotificationType.COLLAB_UGC_RECEPTION_CHOICE) {
    const at = notification.i18nParams.cancelAt;
    if (typeof at !== 'string') return notification.i18nParams;
    // The day in the brand's zone, the one the email writes it in (Paris when it set none).
    const zone = notification.i18nParams.cancelTimeZone;
    const day = new Date(at).toLocaleDateString(language, {
      dateStyle: 'long',
      timeZone: typeof zone === 'string' ? zone : 'Europe/Paris',
    });
    return { ...notification.i18nParams, cancelAt: day };
  }
  if (notification.type === NotificationType.COLLAB_UGC_RIGHTS_ENDING) {
    const at = notification.i18nParams.firstRightsEndAt;
    if (typeof at !== 'string') return notification.i18nParams;
    // The UTC day, the one the email names: never a day off for a reader east or west of it.
    const day = new Date(at).toLocaleDateString(language, { dateStyle: 'long', timeZone: 'UTC' });
    return { ...notification.i18nParams, firstRightsEndAt: day };
  }
  if (notification.type !== NotificationType.COLLAB_PAYOUT_COUNTRY_UNSUPPORTED) {
    return notification.i18nParams;
  }
  const code = notification.i18nParams.country;
  if (typeof code !== 'string') return notification.i18nParams;
  return { ...notification.i18nParams, country: countryName(code, language) };
}

function countryName(code: string, language: string): string {
  try {
    const name = new Intl.DisplayNames([language], { type: 'region', fallback: 'none' }).of(code);
    return name ?? code;
  } catch {
    return code;
  }
}

/** A UGC content to review on a running clock: its body names when silence validates it. */
function isUgcContentToReview(notification: NotificationDto): boolean {
  return (
    notification.type === NotificationType.COLLAB_CONTENT_RECORDED &&
    notification.data.collabType === CollabType.UGC &&
    typeof notification.data.tacitValidationDueAt === 'string'
  );
}

/**
 * The body a notification is worded with. A UGC parcel the creator has not
 * received names its tracking number when the shop reported one; a UGC content
 * to review names the date and time it is validated without an answer; a
 * product still not received names the day the collab is cancelled without a
 * choice.
 */
export function getNotificationBodyKey(notification: NotificationDto): string {
  if (isUgcContentToReview(notification)) {
    return 'notifications.collab.contentRecorded.ugcBody';
  }
  if (
    notification.type === NotificationType.COLLAB_UGC_PRODUCT_NOT_RECEIVED &&
    notification.data.trackingNumber
  ) {
    return 'notifications.collab.ugcProductNotReceived.bodyWithTracking';
  }
  if (
    notification.type === NotificationType.COLLAB_UGC_RECEPTION_CHOICE &&
    notification.data.cancelAt
  ) {
    return 'notifications.collab.ugcReceptionChoice.bodyWithCancel';
  }
  return notification.bodyKey;
}
